import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getURLParam } from 'js/utils/url.util';
import { useRemoveQueryParams } from 'js/hooks/navigate';
import { ChangeForm } from 'js/components/common/ChangeForm';
import { componentService } from 'js/services/component.service';
import { withComponent } from 'js/components/hocs/withComponent';
import { useComponentConfig } from 'js/hooks/component/useComponentConfig';
import { DynamicChangePassword } from 'js/components/main/DynamicChangePassword';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as Types from './types';
import * as hooks from './hooks';
import {
    DYNAMIC_FORGOT_PASSWORD,
    DEFAULT_FORGOT_PASSWORD_FORM,
    DEFAULT_VERIFICATION_QUESTION_FORM,
    DEFAULT_CHANGE_PASSWORD_WITHOUT_OLD_FORM
} from './constants';

import style from './DynamicForgotPassword.scss';

export const DynamicForgotPasswordBare: React.FC = () => {
    const { search } = useLocation();
    const translate = useComponentTranslations();
    const removeQueryParams = useRemoveQueryParams();
    const [formError, setFormError] = useState<string>('');
    const [fpActionToken, setFpActionToken] = useState<string>('');
    const [userData, setUserData] = useState<Types.IDynamicForgotPasswordUserData>({});
    const {
        forgotPasswordForm, changePasswordForm, verificationQuestionForm
    } = useComponentConfig<Types.IDynamicForgotPasswordConfig>();
    const onChangePasswordSuccess = hooks.useOnChangePasswordSuccess();
    const forgotPassword = hooks.useForgotPassword({ userData, setUserData });
    const isVerificationQuestionFormAvailable =
        hooks.useIsFormAvailable(verificationQuestionForm || DEFAULT_VERIFICATION_QUESTION_FORM);

    useEffect(() => {
        const actionToken: string | undefined = getURLParam(search, 'fpActionToken');

        if (!actionToken) {
            return;
        }

        setFpActionToken(actionToken);
        removeQueryParams(['fpActionToken']);
    }, [search, fpActionToken, removeQueryParams]);

    hooks.useForgotPasswordByToken(fpActionToken, setFormError);

    if (userData.verificationQuestion) {
        return (
            <>
                {isVerificationQuestionFormAvailable && (
                    <div className={style.verificationQuestionContainer}>
                        <span>{translate('verificationQuestion')}</span>
                        <span>{userData.verificationQuestion}</span>
                    </div>
                )}
                <ChangeForm
                    onSubmit={forgotPassword}
                    key="verificationQuestionStep"
                    formName={verificationQuestionForm || DEFAULT_VERIFICATION_QUESTION_FORM}
                />
            </>
        );
    }

    if (fpActionToken) {
        return (
            <DynamicChangePassword
                isPasFlow
                error={formError}
                key="changePasswordStep"
                onSuccess={onChangePasswordSuccess}
                form={changePasswordForm || DEFAULT_CHANGE_PASSWORD_WITHOUT_OLD_FORM}
            />
        );
    }

    return (
        <ChangeForm
            key="forgotPasswordStep"
            onSubmit={forgotPassword}
            validationOperation="forgotPassword"
            formName={forgotPasswordForm || DEFAULT_FORGOT_PASSWORD_FORM}
        />
    );
};

export const DynamicForgotPassword = withComponent({
    isForLoggedOut: true,
    tkPrefix: 'playtech.mobile.system.dynamic-forgot-password.'
})(DynamicForgotPasswordBare);

componentService.registerPageComponents({ [DYNAMIC_FORGOT_PASSWORD]: DynamicForgotPassword });
